// ==========================================
// CompanyModel.js
// ==========================================
export class CompanyModel {
  /* ─────────────────────────────────────────────────────────────────────
    Allowed keys
  ───────────────────────────────────────────────────────────────────── */
  static ALLOWED_KEYS = Object.freeze([
    // Identity
    "id",
    "companyId",
    "name",
    "address1",
    "address2",
    "address3",
    "city",
    "pincode",
    "country",
    "countryCode",      
    "state",
    "stateCode",        

    // Communication
    "phoneNumber",
    "email",
    "website",
    "contactPerson",         
    "contactPersonName",
    "contactPersonMobile",
    "gstin",
    "assets",
    "userCount",
    "status",
    "localSupabaseURL",
    "localSupabaseAnonKey",
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD = "companyId";
  static NAME_FIELD = "name";

  /** Company codes: 2–32 chars, uppercase letters / digits / `_` / `-`. */
  static COMPANY_CODE_REGEX = /^[A-Z0-9_-]{2,32}$/;

  /** Loose email check (mirrors HTML5 input[type=email]). */
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Phone: 10–15 digits after stripping formatting chars. */
  static PHONE_REGEX = /^\d{10,15}$/;

  constructor(data = {}, { isCreation = false } = {}) {
    /* ── 1. Seed defaults for every allowed key ───────────────────────── */
    for (const key of CompanyModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "Active";
      } else if (key === "assets" || key === "userCount") {
        this[key] = 0;
      } else {
        this[key] = null;
      }
    }

    /* ── 2. Population ─────────────────────────────────────────────────── */
    if (isCreation) {
      const now = new Date().toISOString();

      // Identity
      this.id        = data.id        || data.companyId || null;
      this.companyId = data.companyId || data.id        || null;
      this.name      = data.name      || null;

      // Geographic
      this.address     = data.address     || data.address1 || null;
      this.address1    = data.address1    || data.address  || null;
      this.address2    = data.address2    || null;
      this.address3    = data.address3    || null;
      this.city        = data.city        || null;
      this.pincode     = data.pincode     || null;
      this.country     = data.country     || null;
      this.countryCode = data.countryCode || "IN";
      this.state       = data.state       || null;
      this.stateCode   = data.stateCode   || null;

      // Communication
      this.phoneNumber         = data.phoneNumber         || null;
      this.email               = data.email               || null;
      this.website             = data.website             || null;
      this.contactPerson       = data.contactPerson       || data.contactPersonName || null;
      this.contactPersonName   = data.contactPersonName   || data.contactPerson     || null;
      this.contactPersonMobile = data.contactPersonMobile || null;

      // Compliance
      this.gstin = data.gstin || null;

      // Metrics
      this.assets    = data.assets    ?? 0;
      this.userCount = data.userCount ?? 0;
      this.status    = data.status    || "Active";

      // Local infra
      this.localSupabaseURL     = data.localSupabaseURL     || null;
      this.localSupabaseAnonKey = data.localSupabaseAnonKey || null;

      // Metadata (nested audit only)
      this.metadata = {
        createdAt:   data.metadata?.createdAt   || now,
        createdBy:   data.metadata?.createdBy   || "system",
        updatedAt:   data.metadata?.updatedAt   || now,
        updatedBy:   data.metadata?.updatedBy   || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      /* ── Update path — copy only whitelisted keys ─────────────────── */
      for (const key of Object.keys(data)) {
        if (!CompanyModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else {
          this[key] = data[key];
        }
      }
    }

    /* ── 3. Alias symmetry ─────────────────────────────────────────────── */
    if (this.address1 && !this.address) this.address = this.address1;
    if (this.address && !this.address1) this.address1 = this.address;
    if (this.contactPersonName && !this.contactPerson)
      this.contactPerson = this.contactPersonName;
    if (this.contactPerson && !this.contactPersonName)
      this.contactPersonName = this.contactPerson;
  }

  /* ─────────────────────────────────────────────────────────────────────
     VALIDATION — mirrors modal's validateForm() 1:1
  ───────────────────────────────────────────────────────────────────── */
  isValid() {
    // Name
    if (!this.name || !String(this.name).trim()) return false;

    // Company code
    if (!this.companyId || !String(this.companyId).trim()) return false;
    if (!CompanyModel.COMPANY_CODE_REGEX.test(this.companyId)) return false;

    // Address line 1
    if (!this.address1 || !String(this.address1).trim()) return false;

    // Country & state
    if (!this.countryCode) return false;
    if (!this.stateCode)   return false;

    // City
    if (!this.city || !String(this.city).trim()) return false;

    // Pincode
    if (!this.pincode || !String(this.pincode).trim()) return false;
    if (this.countryCode === "IN" && !/^\d{6}$/.test(this.pincode)) return false;

    // Phone
    if (!this.phoneNumber) return false;
    const phoneDigits = String(this.phoneNumber).replace(/[\s\-()]/g, "");
    if (!CompanyModel.PHONE_REGEX.test(phoneDigits)) return false;

    // Optional contact person mobile
    if (this.contactPersonMobile) {
      const mobileDigits = String(this.contactPersonMobile).replace(
        /[\s\-()]/g,
        "",
      );
      if (!CompanyModel.PHONE_REGEX.test(mobileDigits)) return false;
    }

    // Optional email format check
    if (
      this.email &&
      this.email !== "—" &&
      !CompanyModel.EMAIL_REGEX.test(this.email)
    ) {
      return false;
    }

    return true;
  }

  /* ─────────────────────────────────────────────────────────────────────
     SERIALISATION
  ───────────────────────────────────────────────────────────────────── */
  toPayload() {
    const payload = {};
    for (const key of CompanyModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of CompanyModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}